import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import ContentWrapper from '../components/contentwrapper/ContentWrapper'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { fetchAllDataAPI } from '../utils/api'
import { toast } from 'react-toastify'
import { numberFormat } from '../components/numberFormat'
import { MdLocalShipping, MdVerified, MdArrowBack } from 'react-icons/md'
import { FaBoxOpen, FaMapMarkerAlt, FaUser, FaMobile, FaRupeeSign, FaTruck } from 'react-icons/fa'
import { CgTrack } from 'react-icons/cg'
import withAuth from '../utils/withAuth'

const STATUS_CONFIG = {
    'Delivered': { color: '#0e7c52', bg: '#e8f5e9', dot: '#0e7c52' },
    'RTO Delivered': { color: '#597e0d', bg: '#f1f8e9', dot: '#597e0d' },
    'In Transit': { color: '#1565c0', bg: '#e3f2fd', dot: '#1565c0' },
    'Out For Delivery': { color: '#00838f', bg: '#e0f7fa', dot: '#00838f' },
    'Cancelled': { color: '#c62828', bg: '#ffebee', dot: '#c62828' },
    'NEW': { color: '#6a1b9a', bg: '#f3e5f5', dot: '#6a1b9a' },
    'Undelivered': { color: '#e65100', bg: '#fff3e0', dot: '#e65100' },
    'Pickup Booked': { color: '#4e9191', bg: '#e0f4f4', dot: '#4e9191' },
    'default': { color: '#555', bg: '#f5f5f5', dot: '#999' },
};
const getStatusStyle = (s) => STATUS_CONFIG[s] || STATUS_CONFIG['default'];

const CONFIRM_CONFIG = {
    'Confirm Order': { color: '#0e7c52', bg: '#e8f5e9' },
    'Confirm': { color: '#0e7c52', bg: '#e8f5e9' },
    'Confirmed': { color: '#0e7c52', bg: '#e8f5e9' },
    'Abandoned': { color: '#c62828', bg: '#ffebee' },
    'Pending': { color: '#e65100', bg: '#fff3e0' },
    'default': { color: '#555', bg: '#f5f5f5' },
};
const getConfirmStyle = (s) => CONFIRM_CONFIG[s] || CONFIRM_CONFIG['default'];

const InfoRow = ({ icon, label, value, mono }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
        <div style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: '#f0f9f9', color: '#4e9191',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: '14px'
        }}>{icon}</div>
        <div>
            <div style={{ fontSize: '10px', color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            <div style={{ fontSize: '13px', color: '#333', fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit' }}>{value || '—'}</div>
        </div>
    </div>
);

const SectionCard = ({ title, icon, children, accent }) => (
    <div style={{
        background: '#fff', borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        overflow: 'hidden', marginBottom: '20px'
    }}>
        <div style={{
            padding: '14px 20px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex', alignItems: 'center', gap: '10px',
            background: accent || '#fafafa'
        }}>
            <span style={{ color: '#4e9191', fontSize: '16px' }}>{icon}</span>
            <span style={{ fontWeight: 700, fontSize: '13px', color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
        </div>
        <div style={{ padding: '18px 20px' }}>{children}</div>
    </div>
);

const OrderDetail = () => {
    const { ordernumber } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const subagentid = localStorage.getItem('subagentdata');

    useEffect(() => {
        if (!subagentid) { navigate('/'); return; }
        getOrderDetail();
    }, [ordernumber]);

    const getOrderDetail = () => {
        setLoading(true);
        fetchAllDataAPI(`sub-agent-get-order-detail/${ordernumber}`)
            .then((res) => {
                if (res?.data?.status === 1) {
                    setOrder(res.data.order);
                    setProducts(res.data.products || []);
                } else {
                    toast.warning('Order not found');
                }
            })
            .catch(() => toast.error('Failed to load order'))
            .finally(() => setLoading(false));
    };

    const handlePrint = () => window.print();

    if (loading) return (
        <div className="misDashboard">
            <ContentWrapper>
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    {[...Array(4)].map((_, i) => (
                        <div key={i} style={{
                            height: '120px', borderRadius: '16px', marginBottom: '16px',
                            background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',
                            backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite'
                        }} />
                    ))}
                </div>
            </ContentWrapper>
        </div>
    );

    if (!order) return (
        <div className="misDashboard">
            <ContentWrapper>
                <div style={{ textAlign: 'center', padding: '60px', color: '#bbb' }}>Order not found</div>
            </ContentWrapper>
        </div>
    );

    const statusStyle = getStatusStyle(order.shipment_status);
    const confirmStyle = getConfirmStyle(order.order_confirm_status);
    const totalAmount = products.reduce((s, p) => s + parseFloat(p.product_total_price || 0), 0);

    return (
        <div className="misDashboard">
            <Helmet><title>Order #{order.order_number} — Sat Kartar</title></Helmet>

            <style>{`
                @keyframes shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .order-detail-page { animation: fadeInUp 0.4s ease; }

                /* Hero Banner */
                .order-hero {
                    background: linear-gradient(135deg, #4e9191 0%, #0e7c52 50%, #597e0d 100%);
                    border-radius: 20px;
                    padding: 28px 32px;
                    color: #fff;
                    margin-bottom: 24px;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 8px 32px rgba(78,145,145,0.3);
                }
                .order-hero::before {
                    content: '';
                    position: absolute; top: -40px; right: -40px;
                    width: 200px; height: 200px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.06);
                }
                .order-hero::after {
                    content: '';
                    position: absolute; bottom: -60px; right: 80px;
                    width: 150px; height: 150px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.04);
                }

                /* Product table */
                .product-table { width: 100%; border-collapse: collapse; }
                .product-table th {
                    background: #f8f9fa; padding: 11px 14px;
                    font-size: 11px; font-weight: 700; color: #888;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #eee; text-align: left;
                }
                .product-table td {
                    padding: 13px 14px; font-size: 13px; color: #444;
                    border-bottom: 1px solid #f5f5f5; vertical-align: middle;
                }
                .product-table tr:last-child td { border-bottom: none; }
                .product-table tr:hover td { background: #f8fffe; }

                /* Timeline */
                .timeline-dot {
                    width: 12px; height: 12px; border-radius: 50%;
                    display: inline-block; margin-right: 8px;
                    flex-shrink: 0; margin-top: 4px;
                }

                /* Print styles */
                @media print {
                    .no-print { display: none !important; }
                    .order-detail-page { animation: none; }
                    .order-hero { background: #4e9191 !important; -webkit-print-color-adjust: exact; }
                }
            `}</style>

            <ContentWrapper>
                <div className="order-detail-page" style={{marginBottom:"50px"}}>

                    {/* Breadcrumb + Actions */}
                    <div className="d-flex justify-content-between align-items-center mb-3 no-print">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                                <li className="breadcrumb-item"><Link to="/all-orders">All Orders</Link></li>
                                <li className="breadcrumb-item">#{order.order_number}</li>
                            </ol>
                        </nav>
                        <div className="d-flex gap-2">
                            <button onClick={() => navigate(-1)} className="btn btn-sm"
                                style={{ background: '#f5f5f5', color: '#555', borderRadius: '10px', fontWeight: 600, fontSize: '12px' }}>
                                <MdArrowBack style={{ marginRight: '4px' }} /> Back
                            </button>
                            {/* <button onClick={handlePrint} className="btn btn-sm"
                                style={{ background: '#4e9191', color: '#fff', borderRadius: '10px', fontWeight: 600, fontSize: '12px' }}>
                                <MdPrint style={{ marginRight: '4px' }} /> Print
                            </button> */}
                        </div>
                    </div>

                    {/* Hero Banner */}
                    <div className="order-hero">
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                    <FaBoxOpen style={{ fontSize: '28px', opacity: 0.9 }} />
                                    <div>
                                        <div style={{ fontSize: '11px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>Order Number</div>
                                        <div style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '1px' }}>#{order.order_number}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '12px' }}>
                                    <div style={{ fontSize: '12px', opacity: 0.85 }}>
                                        📅 {order.created_date} at {order.created_time}
                                    </div>
                                    <div style={{ fontSize: '12px', opacity: 0.85 }}>
                                        🏷️ {order.vendor_name}
                                    </div>
                                    {order.payment_method && (
                                        <div style={{ fontSize: '12px', opacity: 0.85 }}>
                                            💳 {order.payment_method}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
                                <div style={{ fontSize: '11px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>Order Total</div>
                                <div style={{ fontSize: '32px', fontWeight: 800 }}>
                                    {numberFormat(order.subtotal_price)}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '10px', flexWrap: 'wrap' }}>
                                    <span style={{
                                        background: statusStyle.bg, color: statusStyle.color,
                                        padding: '4px 12px', borderRadius: '20px',
                                        fontSize: '11px', fontWeight: 700
                                    }}>
                                        ● {order.shipment_status || 'New Order'}
                                    </span>
                                    <span style={{
                                        background: confirmStyle.bg, color: confirmStyle.color,
                                        padding: '4px 12px', borderRadius: '20px',
                                        fontSize: '11px', fontWeight: 700
                                    }}>
                                        <MdVerified style={{ marginRight: '3px' }} />
                                        {order.order_confirm_status || 'Pending'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* Left Column */}
                        <div className="col-lg-8">

                            {/* Products */}
                            <SectionCard title="Order Items" icon={<FaBoxOpen />}>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="product-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Item</th>
                                                <th>SKU</th>
                                                <th>Variant</th>
                                                <th>Qty</th>
                                                <th>Price</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} style={{ textAlign: 'center', color: '#bbb', padding: '24px' }}>
                                                        No products found
                                                    </td>
                                                </tr>
                                            ) : products.map((p, i) => (
                                                <tr key={i}>
                                                    <td style={{ color: '#aaa', fontSize: '12px' }}>{i + 1}</td>
                                                    <td>
                                                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#333' }}>{p.product_title || p.product_name}</div>
                                                        {p.product_title !== p.product_name && (
                                                            <div style={{ fontSize: '11px', color: '#aaa' }}>{p.product_name}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            background: '#f3e5f5', color: '#6a1b9a',
                                                            padding: '2px 8px', borderRadius: '6px',
                                                            fontSize: '11px', fontWeight: 600, fontFamily: 'monospace'
                                                        }}>{p.product_sku || '—'}</span>
                                                    </td>
                                                    <td style={{ color: '#888', fontSize: '12px' }}>{p.product_variant_title || '—'}</td>
                                                    <td>
                                                        <span style={{
                                                            background: '#e3f2fd', color: '#1565c0',
                                                            padding: '2px 10px', borderRadius: '20px',
                                                            fontSize: '12px', fontWeight: 700
                                                        }}>{p.product_quantity}</span>
                                                    </td>
                                                    <td style={{ color: '#555' }}>{numberFormat(p.product_price)}</td>
                                                    <td style={{ fontWeight: 700, color: '#0e7c52' }}>
                                                        {numberFormat(p.product_total_price)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        {products.length > 0 && (
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={5} />
                                                    <td style={{ fontWeight: 700, fontSize: '13px', color: '#333', paddingTop: '14px', borderTop: '2px solid #eee' }}>
                                                        Order Total
                                                    </td>
                                                    <td style={{ fontWeight: 800, fontSize: '15px', color: '#0e7c52', paddingTop: '14px', borderTop: '2px solid #eee' }}>
                                                        {numberFormat(order.subtotal_price)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </SectionCard>

                            {/* Shipping Address */}
                            <SectionCard title="Delivery Address" icon={<FaMapMarkerAlt />}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <InfoRow icon={<FaUser />} label="Customer Name" value={order.cust_name} />
                                        <InfoRow icon={<FaMobile />} label="Mobile" value={order.cust_phone} mono />
                                        <InfoRow icon={<FaMapMarkerAlt />} label="Address" value={[order.address1, order.address2].filter(Boolean).join(', ')} />
                                    </div>
                                    <div className="col-md-6">
                                        <InfoRow icon={<span>🏙️</span>} label="City" value={order.city} />
                                        <InfoRow icon={<span>🗺️</span>} label="State" value={order.state} />
                                        <InfoRow icon={<span>📮</span>} label="Pincode" value={order.pincode} mono />
                                        <InfoRow icon={<span>🌍</span>} label="Country" value={order.country} />
                                    </div>
                                </div>
                            </SectionCard>
                        </div>

                        {/* Right Column */}
                        <div className="col-lg-4">

                            {/* Shipping Info */}
                            <SectionCard title="Shipping Info" icon={<FaTruck />}>
                                <InfoRow icon={<MdLocalShipping />} label="Shipping ID" value={order.shipingid} mono />
                                <InfoRow icon={<CgTrack />} label="AWB Number" value={order.awb_number} mono />
                                <InfoRow icon={<span>#</span>} label="CRM Order ID" value={order.crm_order_id} mono />
                                <InfoRow icon={<span>📦</span>} label="Status"
                                    value={
                                        <span style={{
                                            background: statusStyle.bg, color: statusStyle.color,
                                            padding: '3px 10px', borderRadius: '20px',
                                            fontSize: '11px', fontWeight: 600
                                        }}>
                                            {order.shipment_status || 'New Order'}
                                        </span>
                                    }
                                />
                            </SectionCard>

                            {/* Payment Info */}
                            <SectionCard title="Payment Info" icon={<FaRupeeSign />}>
                                <InfoRow icon={<span>💳</span>} label="Payment Method" value={order.payment_method} />
                                <InfoRow icon={<span>💰</span>} label="Subtotal" value={numberFormat(order.subtotal_price)} />
                                <InfoRow icon={<span>🏷️</span>} label="Discount" value={numberFormat(order.total_discounts)} />
                                <InfoRow icon={<span>✅</span>} label="Financial Status" value={order.financial_status} />
                                <div style={{
                                    background: 'linear-gradient(135deg, #4e9191, #0e7c52)',
                                    borderRadius: '12px', padding: '14px 16px',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    marginTop: '8px'
                                }}>
                                    <span style={{ color: '#fff', fontWeight: 600, fontSize: '13px' }}>Total Amount</span>
                                    <span style={{ color: '#fff', fontWeight: 800, fontSize: '20px' }}>
                                        {numberFormat(order.subtotal_price)}
                                    </span>
                                </div>
                            </SectionCard>

                            {/* UTM Tracking */}
                            {(order.utm_source || order.utm_medium || order.utm_campaign) && (
                                <SectionCard title="UTM Tracking" icon={<span>📊</span>}>
                                    <InfoRow icon={<span>🔗</span>} label="Source" value={order.utm_source} />
                                    <InfoRow icon={<span>📡</span>} label="Medium" value={order.utm_medium} />
                                    <InfoRow icon={<span>🎯</span>} label="Campaign" value={order.utm_campaign} />
                                </SectionCard>
                            )}

                            {/* Order Meta */}
                            <SectionCard title="Order Meta" icon={<span>🔖</span>}>
                                <InfoRow icon={<span>🏪</span>} label="Platform" value={order.vendor_name} />
                                <InfoRow icon={<span>🌐</span>} label="Source" value={order.source_name} />
                                <InfoRow icon={<span>📅</span>} label="Order Date" value={`${order.created_date} ${order.created_time}`} />
                                <InfoRow icon={<MdVerified />} label="Confirm Status"
                                    value={
                                        <span style={{
                                            background: confirmStyle.bg, color: confirmStyle.color,
                                            padding: '3px 10px', borderRadius: '20px',
                                            fontSize: '11px', fontWeight: 600
                                        }}>
                                            {order.order_confirm_status || '—'}
                                        </span>
                                    }
                                />
                            </SectionCard>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(OrderDetail);